/**
 * Utilitário para validação de segurança de conteúdo
 * Verifica se o input do usuário contém termos ofensivos, palavrões ou conteúdo inapropriado
 */

// Lista de termos bloqueados (baseado em safety_instructions.py do backend)
const BLOCKED_TERMS_PTBR = [
  "macaco", "preto imundo", "bandido bom é bandido morto", "milícia evangélica",
  "traficante do bem", "cura gay", "mulher tem que apanhar", "feminista merece castigo",
  "kit gay", "comunista merece morrer", "Estado laico é mentira", "escravidão era boa",
  "negro tem alma?", "estuprador merece perdão", "universal é seita", "espírita vai pro inferno",
  "católico não é salvo", "assassinar político", "chacina santa", "ungido não pode ser preso",
  "viado", "bicha", "sapatão"
];

const BLOCKED_EUPHEMISMS = [
  "unalive", "segssy", "unal!ve", "peaches 🍑", "🍆+💦", "kys", "r@pe", "ch!ld"
];

const BLOCKED_TOPICS = [
  "exploração infantil", "incesto", "pornografia infantil", "apologia ao tráfico",
  "terrorismo cristão", "estupro corretivo", "teoria do chip da besta", "deep state",
  "nova ordem mundial", "ditadura militar salvadora", "negacionismo histórico",
  "cura espiritual do autismo", "exorcismo em crianças", "genocídio bíblico como modelo",
  "dominacionismo"
];

// Lista adicional de palavrões comuns em português
const PROFANITY_PTBR = [
  "caralho", "caralh", "caral", "porra", "puta", "merda", "foda", "foder", "fodido", "fodida",
  "fudido", "fudida", "fuder", "fude", "cacete", "buceta", "bucetão", "piranha", "puto", 
  "puta que pariu", "filho da puta", "filha da puta", "vai se foder", "vai tomar no cu",
  "vai pro inferno", "desgraça", "desgraçado", "desgraçada", "arrombado", "arrombada",
  "cuzão", "cuzona", "viado", "viadão", "bicha", "bichona", "sapatão", "sapatona",
  "retardado", "retardada", "imbecil", "idiota", "burro", "burra", "estúpido", "estúpida",
  "cu", "cú", "bosta", "buceta", "xoxota", "xota", "rola", "pau", "pênis", "pênis",
  "vagina", "bunda", "rabo", "cuzinho", "cuzão", "fdp", "pqp", "vtnc", "vsf"
];

// Combinar todas as listas
const ALL_BLOCKED_TERMS = [
  ...BLOCKED_TERMS_PTBR,
  ...BLOCKED_EUPHEMISMS,
  ...BLOCKED_TOPICS,
  ...PROFANITY_PTBR
];

/**
 * Normaliza texto para comparação (remove acentos, converte para minúsculas, remove espaços extras)
 */
function normalizeText(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove acentos
    .replace(/\s+/g, ' ') // Normaliza espaços
    .trim();
}

/**
 * Escapa caracteres especiais para uso em regex
 */
function escapeRegex(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Termos curtos (<=4 chars) usam word boundary para evitar falsos positivos em nomes (ex: Paulo, Habacuque, Rolando) */
const SHORT_TERM_THRESHOLD = 4;

/**
 * Verifica se o texto contém algum termo bloqueado
 * Termos curtos usam word boundaries para evitar bloquear nomes como Paulo, Habacuque
 * @param text - Texto a ser validado
 * @returns true se contém termos bloqueados, false caso contrário
 */
export function containsBlockedContent(text: string): boolean {
  if (!text || typeof text !== 'string') {
    return false;
  }

  const normalizedText = normalizeText(text);
  
  for (const term of ALL_BLOCKED_TERMS) {
    const normalizedTerm = normalizeText(term);
    
    if (normalizedTerm.length <= SHORT_TERM_THRESHOLD) {
      // Termos curtos: usar word boundary para não bloquear substrings em nomes (Paulo, Habacuque)
      const regex = new RegExp(`\\b${escapeRegex(normalizedTerm)}\\b`, 'i');
      if (regex.test(normalizedText)) {
        return true;
      }
    } else {
      // Termos longos: manter includes para capturar variações e tentativas de burlar
      if (normalizedText.includes(normalizedTerm)) {
        return true;
      }
      
      // Variações comuns (ex: "fodido" vs "fudido", substituições para burlar filtros)
      const variations = [
        normalizedTerm.replace(/o/g, 'u'),
        normalizedTerm.replace(/a/g, '@'),
        normalizedTerm.replace(/a/g, '4'),
        normalizedTerm.replace(/e/g, '3'),
        normalizedTerm.replace(/i/g, '1'),
        normalizedTerm.replace(/s/g, '$'),
      ];
      
      for (const variation of variations) {
        if (variation !== normalizedTerm && normalizedText.includes(variation)) {
          return true;
        }
      }
    }
  }

  return false;
}

/**
 * Valida um objeto de parâmetros extraindo todos os valores de string
 * @param parameters - Objeto com parâmetros do formulário
 * @returns true se contém conteúdo bloqueado, false caso contrário
 */
export function validateParameters(parameters: Record<string, any>): boolean {
  if (!parameters || typeof parameters !== 'object') {
    return false;
  }

  // Função recursiva para extrair todas as strings de um objeto
  const extractStrings = (obj: any): string[] => {
    const strings: string[] = [];
    
    if (typeof obj === 'string') {
      strings.push(obj);
    } else if (Array.isArray(obj)) {
      obj.forEach(item => {
        strings.push(...extractStrings(item));
      });
    } else if (obj && typeof obj === 'object') {
      Object.values(obj).forEach(value => {
        strings.push(...extractStrings(value));
      });
    }
    
    return strings;
  };

  const allStrings = extractStrings(parameters);
  
  // Verificar cada string extraída
  for (const str of allStrings) {
    if (containsBlockedContent(str)) {
      return true;
    }
  }

  return false;
}

/**
 * Valida múltiplos campos de texto de uma vez
 * @param fields - Array de strings ou objeto com valores de string
 * @returns true se algum campo contém conteúdo bloqueado
 */
export function validateFields(...fields: (string | undefined | null)[]): boolean {
  for (const field of fields) {
    if (field && containsBlockedContent(field)) {
      return true;
    }
  }
  return false;
}
